import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { authService } from '@/services/auth';
import { useLanguage } from '@/state/language/LanguageProvider';
import { PublicHeader } from '@/components/public/PublicHeader';

export const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { translateWithFallback } = useLanguage();

  // English fallbacks so this public page never shows raw key paths if a key is missing
  const tf = (key: string, fallback: string, params?: Record<string, string | number>) =>
    translateWithFallback(key, fallback, params);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await authService.requestPasswordReset(email);
      setEmailSent(true);
      toast.success(
        tf('auth.passwordResetEmailSent', 'Password reset email sent successfully')
      );
    } catch (error) {
      console.error('Password reset request error:', error);
      toast.error(
        tf(
          'auth.passwordResetEmailFailed',
          'Failed to send password reset email. Please try again.'
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="forgot-password-page min-h-screen bg-gray-100 flex flex-col">
      <PublicHeader />

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full space-y-8 text-center">
          <div>
            <h1 className="text-4xl font-bold text-[#1a5d2e] mb-2">
              {tf('auth.forgotPassword', 'Forgot Password')}
            </h1>
            <p className="text-lg text-gray-700">
              {emailSent
                ? tf(
                    'auth.checkEmailForReset',
                    'Check your email for password reset instructions'
                  )
                : tf(
                    'auth.enterEmailToReset',
                    'Enter your email address and we will send you a link to reset your password'
                  )}
            </p>
          </div>

          <div className="flex justify-center">
            <div className="w-20 h-20 bg-[#1a5d2e] rounded-full flex items-center justify-center">
              <svg
                className="h-10 w-10 text-white"
                viewBox="0 0 24 24"
                style={{ width: '50px', height: '50px' }}
              >
                <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z" />
              </svg>
            </div>
          </div>

          {!emailSent ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="flex items-center space-x-3">
                <EnvelopeIcon className="h-5 w-5 text-[#1a5d2e] flex-shrink-0" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={tf('auth.emailPlaceholder', 'Enter your email')}
                  className="flex-1 py-3 px-3 border-2 border-[#1a5d2e] rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1a5d2e] focus:border-transparent text-center"
                  required
                />
              </div>

              <div className="text-left">
                <Link
                  to="/"
                  className="text-[#1a5d2e] underline hover:text-[#0d5d2c] transition-colors"
                >
                  {tf('auth.backToLogin', 'Back to Login')}
                </Link>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#1a5d2e] text-white py-3 px-4 rounded-lg font-semibold text-lg hover:bg-[#0d5d2c] focus:outline-none focus:ring-2 focus:ring-[#1a5d2e] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading
                  ? tf('auth.sending', 'Sending...')
                  : tf('auth.sendResetEmail', 'Send Reset Email')}
              </button>
            </form>
          ) : (
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <p className="text-gray-900 mb-4">
                  {tf(
                    'auth.passwordResetLinkSent',
                    'Password reset link has been sent to {email}. Please check your inbox and follow the instructions.',
                    { email }
                  )}
                </p>
                <p className="text-gray-600 text-sm">
                  {tf(
                    'auth.linkExpiration',
                    'The link will expire in 1 hour for security reasons.'
                  )}
                </p>
              </div>

              <Link
                to="/"
                className="block w-full bg-[#1a5d2e] text-white py-3 px-4 rounded-lg font-semibold text-lg hover:bg-[#0d5d2c] focus:outline-none focus:ring-2 focus:ring-[#1a5d2e] focus:ring-offset-2 transition-colors text-center"
              >
                {tf('auth.backToLogin', 'Back to Login')}
              </Link>

              <button
                onClick={() => setEmailSent(false)}
                className="text-[#1a5d2e] underline hover:text-[#0d5d2c] transition-colors"
              >
                {tf('auth.didNotReceiveEmail', "Didn't receive the email?")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
